import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Stack,
  Chip,
} from "@mui/material";
import {
  Search,
  Download,
  ArrowBack,
  PeopleAlt,
  CheckCircle,
  PendingActions,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import adminService from "../../redux/admin/adminService";
import { colors } from "../../constants/colors";

interface KYCRow {
  name: string;
  aumUserId: string;
  accountType: string;
  status: string;
  initiatedAt: string;
}

interface SummaryData {
  totalKYCInitiated: number;
  completedKYC: number;
  pendingKYC: number;
}

const KYCInitiated: React.FC = () => {
  const navigate = useNavigate();
  const [dateType, setDateType] = useState<string>("7day");
  
  // Custom date inputs
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<SummaryData>({
    totalKYCInitiated: 0,
    completedKYC: 0,
    pendingKYC: 0,
  });
  const [kycList, setKycList] = useState<KYCRow[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Cache to store already fetched data
  const cacheRef = useRef<Record<string, { summary: SummaryData; kycList: KYCRow[] }>>({});

  // Helper to calculate date ranges in milliseconds
  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();

    switch (dateType) {
      case "1day":
        start.setHours(0, 0, 0, 0);
        break;
      case "7day":
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case "1month":
        start.setMonth(end.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "1year":
        start.setFullYear(end.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "max":
        start.setFullYear(2000, 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          const customStart = new Date(customStartDate);
          customStart.setHours(0, 0, 0, 0);
          const customEnd = new Date(customEndDate);
          customEnd.setHours(23, 59, 59, 999);
          return { startDate: customStart.getTime(), endDate: customEnd.getTime() };
        }
        return null;
      default:
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
    }

    return { startDate: start.getTime(), endDate: end.getTime() };
  }, [dateType, customStartDate, customEndDate]);

  const fetchData = useCallback(async (isCustomSubmit = false) => {
    // Only proceed for custom range if explicitly submitted
    if (dateType === "custom" && !isCustomSubmit) return;

    const range = getDateRange();
    if (!range) return;

    const cacheKey = dateType === "custom" ? `custom_${range.startDate}_${range.endDate}` : dateType;

    // Check Cache first
    if (cacheRef.current[cacheKey]) {
      setSummary(cacheRef.current[cacheKey].summary);
      setKycList(cacheRef.current[cacheKey].kycList);
      return; // Stop here, use cached data
    }

    setLoading(true);
    try {
      const response = await adminService.getSuperAdminOpsKYCInitiated({
        startDate: range.startDate,
        endDate: range.endDate,
      });

      if (response && (response.statusCode === 200 || response.success === 200)) {
        const newSummary = {
          totalKYCInitiated: Number(response.data.totalKYCInitiated || 0),
          completedKYC: Number(response.data.completedKYC || 0),
          pendingKYC: Number(response.data.pendingKYC || 0),
        };
        const newList = response.data.kycList || [];

        setSummary(newSummary);
        setKycList(newList);

        // Store in cache
        cacheRef.current[cacheKey] = { summary: newSummary, kycList: newList };
      } else {
        toast.error(response.message || "Failed to fetch KYC data");
      }
    } catch (error: any) {
      console.error("Error fetching KYC data:", error);
      toast.error(error.message || "Failed to load KYC data");
    } finally {
      setLoading(false);
    }
  }, [getDateRange, dateType]);

  useEffect(() => {
    // Automatically fetch when changing pre-defined ranges
    if (dateType !== "custom") {
      fetchData();
    }
  }, [dateType, fetchData]);

  const handleCustomApply = () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchData(true); // pass true to force submit
  };

  const handleExport = () => {
    if (kycList.length === 0) {
      toast.error("No data to export");
      return;
    }

    const exportData = kycList.map((user) => ({
      "Name": user.name,
      "AUM User ID": user.aumUserId,
      "Account Type": user.accountType,
      "Status": user.status,
      "Initiated At": user.initiatedAt,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "KYC Initiated Report");
    XLSX.writeFile(workbook, `KYC_Initiated_${dateType}_${Date.now()}.xlsx`);
  };

  const filteredUsers = kycList.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.aumUserId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const completedList = filteredUsers.filter((u) => u.status === "COMPLETED");
  const pendingList = filteredUsers.filter((u) => u.status === "PENDING");

  const displayedList = tabValue === 0 ? filteredUsers : (tabValue === 1 ? completedList : pendingList);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs / Back button */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/super-admin/ops-dashboard");
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
            Ops Dashboard
          </Link>
          <Typography color="text.primary">KYC Initiated</Typography>
        </Breadcrumbs>

        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary }}>
          KYC Initiated Investors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          View details of investors who started their KYC process during the selected period.
        </Typography>
      </Box>

      {/* Date Range Selector */}
      <Card sx={{ mb: 4, borderRadius: "12px", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}>
                Select Range Type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  { value: "1day", label: "1 Day" },
                  { value: "7day", label: "7 Days" },
                  { value: "1month", label: "1 Month" },
                  { value: "1year", label: "1 Year" },
                  { value: "max", label: "Max" },
                  { value: "custom", label: "Custom Range" },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={dateType === type.value ? "contained" : "outlined"}
                    onClick={() => setDateType(type.value)}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      px: 3,
                      fontWeight: 600,
                    }}
                  >
                    {type.label}
                  </Button>
                ))}
              </Stack>
            </Grid>

            {dateType === "custom" && (
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Custom Range Dates
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleCustomApply}
                    sx={{ fontWeight: 600, minWidth: "90px" }}
                    disabled={loading || !customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total KYC Initiated
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: colors.primary }}>
                  {loading ? <CircularProgress size={24} /> : summary.totalKYCInitiated}
                </Typography>
              </Box>
              <PeopleAlt sx={{ fontSize: 40, color: colors.primary, opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Completed KYC
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "success.main" }}>
                  {loading ? <CircularProgress size={24} /> : summary.completedKYC}
                </Typography>
              </Box>
              <CheckCircle sx={{ fontSize: 40, color: "success.main", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Pending KYC
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "warning.main" }}>
                  {loading ? <CircularProgress size={24} /> : summary.pendingKYC}
                </Typography>
              </Box>
              <PendingActions sx={{ fontSize: 40, color: "warning.main", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table and Filters */}
      <Paper elevation={3} sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <TextField
            placeholder="Search by Name or AUM User ID..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: "300px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={loading || kycList.length === 0}
              sx={{ fontWeight: 600 }}
            >
              Export Excel
            </Button>
          </Stack>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="user type tabs">
            <Tab label={`All (${filteredUsers.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`Completed (${completedList.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`Pending (${pendingList.length})`} sx={{ fontWeight: 600 }} />
          </Tabs>
        </Box>

        <TableContainer sx={{ maxHeight: "550px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Investor Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  AUM User ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Account Type
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Initiated Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography mt={2} color="textSecondary">
                      Fetching KYC records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : displayedList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary" fontSize={15}>
                      No KYC records found matching your selection in this category.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedList.map((user) => (
                  <TableRow key={user.aumUserId + user.initiatedAt} hover>
                    <TableCell sx={{ py: 2, fontWeight: 600 }}>{user.name}</TableCell>
                    <TableCell sx={{ py: 2 }}>{user.aumUserId}</TableCell>
                    <TableCell sx={{ py: 2 }}>{user.accountType}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.status}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: user.status === "COMPLETED" ? "success.light" : "warning.light",
                          color: user.status === "COMPLETED" ? "success.dark" : "warning.dark",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{user.initiatedAt}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default KYCInitiated;
